'use client';

import React, { useRef, useState } from 'react';
import Link from 'next/link';
import {
  Archive,
  ArrowRight,
  Award,
  BookOpen,
  Check,
  ClipboardCheck,
  ClipboardList,
  Eye,
  FileCheck,
  GraduationCap,
  ImagePlus,
  Landmark,
  Lock,
  MapPin,
  UserPlus,
  Users,
} from 'lucide-react';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { trackEvent } from '@/lib/analyticsEvents';

type DemoMode = 'individual' | 'institution';

interface DemoStep {
  label: string;
  connectedLabel: string;
  icon: React.ElementType;
}

interface DemoConfig {
  seed: string;
  seedIcon: React.ElementType;
  steps: DemoStep[];
  finalStatement: string;
  ctaLabel: string;
  ctaHref: string;
  accent: string;
  accentSoft: string;
  accentBorder: string;
}

const TOTAL_STEPS = 6;

const DEMOS: Record<DemoMode, DemoConfig> = {
  individual: {
    seed: 'Graduation, 2022',
    seedIcon: GraduationCap,
    steps: [
      { label: 'Add the story', connectedLabel: 'the story', icon: BookOpen },
      { label: 'Connect a person', connectedLabel: 'a person', icon: UserPlus },
      { label: 'Place it on the Journey Map', connectedLabel: 'the Journey Map', icon: MapPin },
      { label: 'Attach media', connectedLabel: 'media', icon: ImagePlus },
      { label: 'Ask a witness', connectedLabel: 'a witness', icon: Eye },
      {
        label: 'Add institutional recognition',
        connectedLabel: 'institutional recognition',
        icon: Award,
      },
    ],
    finalStatement: 'One moment becomes part of a life.',
    ctaLabel: 'Start your living record',
    ctaHref: '/register',
    accent: '#e88a73',
    accentSoft: 'rgba(232,138,115,0.14)',
    accentBorder: 'rgba(232,138,115,0.42)',
  },
  institution: {
    seed: 'Community Leadership Program',
    seedIcon: Landmark,
    steps: [
      { label: 'Define the program', connectedLabel: 'the program', icon: ClipboardList },
      { label: 'Add participants', connectedLabel: 'participants', icon: Users },
      { label: 'Collect evidence', connectedLabel: 'evidence', icon: FileCheck },
      { label: 'Review records', connectedLabel: 'reviewed records', icon: ClipboardCheck },
      { label: 'Issue recognition', connectedLabel: 'recognition', icon: Award },
      { label: 'Preserve program history', connectedLabel: 'program history', icon: Archive },
    ],
    finalStatement: 'One program becomes a trusted institutional record.',
    ctaLabel: 'Explore institution solutions',
    ctaHref: '/for-institutions',
    accent: '#9a7bb8',
    accentSoft: 'rgba(154,123,184,0.14)',
    accentBorder: 'rgba(154,123,184,0.42)',
  },
};

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1d9e75] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a1614]';

export default function WatchLivingRecordSection() {
  const reducedMotion = useReducedMotion();
  const motion = !reducedMotion;

  const [mode, setMode] = useState<DemoMode>('individual');
  const [progress, setProgress] = useState<Record<DemoMode, number>>({
    individual: 0,
    institution: 0,
  });
  const [rovingIndex, setRovingIndex] = useState(0);
  const hasStartedRef = useRef(false);
  const stepRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const demo = DEMOS[mode];
  const completed = progress[mode];
  const isComplete = completed >= TOTAL_STEPS;

  const markStarted = () => {
    if (hasStartedRef.current) return false;
    hasStartedRef.current = true;
    trackEvent('landing_living_record_demo_started');
    return true;
  };

  const handleModeChange = (next: DemoMode) => {
    markStarted();
    if (next === mode) return;
    setMode(next);
    setRovingIndex(Math.min(progress[next], TOTAL_STEPS - 1));
  };

  const handleStepClick = (index: number) => {
    if (index !== completed) return;
    if (!markStarted()) {
      trackEvent('landing_living_record_demo_step');
    }
    setProgress((prev) => {
      const nextCount = prev[mode] + 1;
      if (nextCount >= TOTAL_STEPS) {
        trackEvent('landing_living_record_demo_completed');
      }
      return { ...prev, [mode]: nextCount };
    });
  };

  const focusStep = (index: number) => {
    const clamped = Math.max(0, Math.min(TOTAL_STEPS - 1, index));
    stepRefs.current[clamped]?.focus();
  };

  const stepTransitionClass = motion
    ? 'transition-[background-color,border-color,color,transform] duration-200 ease-out'
    : '';
  const connectorTransitionClass = motion ? 'transition-colors duration-300 ease-out' : '';
  const revealClass = motion ? 'animate-slide-up' : '';
  const activeScaleClass = motion ? 'active:scale-[0.97]' : '';
  const toggleTransitionClass = motion
    ? 'transition-[background-color,border-color,color] duration-150 ease-out'
    : '';

  const connectedSoFar = demo.steps.slice(0, completed).map((s) => s.connectedLabel);

  return (
    <section className="relative isolate overflow-hidden py-20 lg:py-28">
      {/* Decorative background only -- sized to inset-0 so it always tracks the section's own
          (content-driven) height. The dark fill is solid so later demo states (more connected-
          record chips, the completed final CTA) never grow into a percentage-based fade zone;
          only shallow, fixed-height strips at the very top/bottom blend into neighbouring
          sections, so interactive controls near the bottom never wash out to white. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 bg-[#0a1614]">
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(120% 65% at 50% 38%, rgba(18,48,39,0.9) 0%, rgba(10,22,20,0.96) 62%, #0a1614 100%)',
          }}
        />
        <div
          className="absolute inset-x-0 top-0 h-16 sm:h-20"
          style={{ background: 'linear-gradient(180deg, #ffffff 0%, #eef4f1 45%, transparent 100%)' }}
        />
        <div
          className="absolute inset-x-0 bottom-0 h-16 sm:h-20"
          style={{ background: 'linear-gradient(0deg, #ffffff 0%, #eef4f1 45%, transparent 100%)' }}
        />
      </div>

      <div className="relative mx-auto max-w-4xl px-6 lg:px-10">
        <div className="text-center">
          <p
            className="mb-4 text-xs font-bold uppercase tracking-[0.28em]"
            style={{ color: '#98f2d3' }}
          >
            A living demonstration
          </p>
          <h2
            className="text-[clamp(1.85rem,3.4vw,2.75rem)] leading-[1.16] text-[#f4efe4]"
            style={{ fontFamily: 'DM Serif Display, serif' }}
          >
            Watch a living record take shape
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base leading-7 text-[#c7d6cf] sm:text-lg sm:leading-8">
            Nothing here is saved. Explore how a single moment grows into a record that can be
            witnessed, verified, and preserved.
          </p>
        </div>

        {/* Individual / Institution toggle */}
        <div
          className="mt-8 flex justify-center"
          role="group"
          aria-label="Choose which kind of living record to watch take shape"
        >
          <div
            className="inline-flex gap-1 rounded-[14px] border p-1"
            style={{ borderColor: 'rgba(152,242,211,0.18)', background: 'rgba(255,255,255,0.03)' }}
          >
            {(['individual', 'institution'] as const).map((option) => {
              const selected = option === mode;
              return (
                <button
                  key={option}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => handleModeChange(option)}
                  className={`rounded-[9px] px-5 py-2.5 text-sm font-bold ${toggleTransitionClass} ${FOCUS_RING} ${
                    selected ? 'text-[#0a1614]' : 'text-[#c7d6cf] hover:text-[#f4efe4]'
                  }`}
                  style={{ background: selected ? '#1d9e75' : 'transparent' }}
                >
                  {option === 'individual' ? 'Individual' : 'Institution'}
                </button>
              );
            })}
          </div>
        </div>

        {/* Seed + growing connected record */}
        <div
          className="mt-8 rounded-[14px] border p-6 sm:p-8"
          style={{ background: 'rgba(255,255,255,0.035)', borderColor: 'rgba(152,242,211,0.16)' }}
        >
          <div className="flex flex-wrap items-center gap-3">
            <span
              className="inline-flex items-center gap-2 rounded-[9px] border px-4 py-2 text-sm font-bold"
              style={{
                background: demo.accentSoft,
                borderColor: demo.accentBorder,
                color: '#f4efe4',
              }}
            >
              <demo.seedIcon size={16} style={{ color: demo.accent }} aria-hidden="true" />
              {demo.seed}
            </span>

            {demo.steps.slice(0, completed).map((step) => (
              <React.Fragment key={step.label}>
                <span
                  aria-hidden="true"
                  className={`h-px w-5 ${connectorTransitionClass}`}
                  style={{ background: demo.accent }}
                />
                <span
                  className={`inline-flex items-center gap-1.5 rounded-[9px] border px-3 py-1.5 text-xs font-semibold text-[#f4efe4] ${
                    motion ? 'animate-fade-in' : ''
                  }`}
                  style={{ background: 'rgba(255,255,255,0.04)', borderColor: demo.accentBorder }}
                >
                  <Check size={12} style={{ color: demo.accent }} aria-hidden="true" />
                  {step.connectedLabel}
                </span>
              </React.Fragment>
            ))}
          </div>

          <p className="mt-4 text-sm leading-6 text-[#9db4aa]" aria-live="polite">
            {completed === 0
              ? 'Choose the first step below to begin connecting this record.'
              : `Now connected: ${connectedSoFar.join(', ')}.`}
          </p>
        </div>

        {/* Step counter + action buttons */}
        <div className="mt-8">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#9db4aa]">
              Build the record
            </p>
            <span
              className="rounded-[9px] border px-2.5 py-1 font-mono text-xs font-medium text-[#98f2d3]"
              style={{ borderColor: 'rgba(29,158,117,0.3)', background: 'rgba(18,55,44,0.5)' }}
            >
              {Math.min(completed + (isComplete ? 0 : 1), TOTAL_STEPS)} of {TOTAL_STEPS}
            </span>
          </div>

          <div
            className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3"
            role="group"
            aria-label={`${mode === 'individual' ? 'Individual' : 'Institution'} living record steps`}
          >
            {demo.steps.map((step, i) => {
              const StepIcon = step.icon;
              const isDone = i < completed;
              const isCurrent = i === completed;
              const isLocked = i > completed;
              const tabIndex = i === rovingIndex ? 0 : -1;

              return (
                <button
                  key={step.label}
                  ref={(el) => {
                    stepRefs.current[i] = el;
                  }}
                  type="button"
                  tabIndex={tabIndex}
                  aria-pressed={isDone}
                  aria-disabled={isLocked}
                  aria-label={`${step.label}${isDone ? ' - added' : isLocked ? ' - locked, complete earlier steps first' : ''}`}
                  onFocus={() => setRovingIndex(i)}
                  onClick={() => handleStepClick(i)}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowRight') {
                      e.preventDefault();
                      focusStep(i + 1);
                    } else if (e.key === 'ArrowLeft') {
                      e.preventDefault();
                      focusStep(i - 1);
                    }
                  }}
                  className={`flex items-center gap-2.5 rounded-[9px] border px-4 py-3 text-left text-sm font-semibold ${stepTransitionClass} ${activeScaleClass} ${FOCUS_RING} ${
                    isLocked ? 'cursor-default' : 'cursor-pointer'
                  }`}
                  style={{
                    background: isDone
                      ? demo.accentSoft
                      : isCurrent
                        ? 'rgba(29,158,117,0.14)'
                        : 'rgba(255,255,255,0.02)',
                    borderColor: isDone
                      ? demo.accentBorder
                      : isCurrent
                        ? 'rgba(29,158,117,0.5)'
                        : 'rgba(255,255,255,0.08)',
                    color: isLocked ? '#6b7f78' : '#f4efe4',
                  }}
                >
                  <span
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                    style={{
                      background: isDone ? demo.accent : isCurrent ? '#1d9e75' : 'transparent',
                      border: isLocked ? '1px solid rgba(255,255,255,0.14)' : 'none',
                    }}
                  >
                    {isDone ? (
                      <Check size={14} className="text-[#0a1614]" aria-hidden="true" />
                    ) : isLocked ? (
                      <Lock size={12} style={{ color: '#6b7f78' }} aria-hidden="true" />
                    ) : (
                      <StepIcon size={14} className="text-[#0a1614]" aria-hidden="true" />
                    )}
                  </span>
                  {step.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Final statement + CTA */}
        {isComplete && (
          <div className={`mt-10 text-center ${revealClass}`}>
            <p
              className="text-[clamp(1.5rem,2.8vw,2.1rem)] leading-[1.2] text-[#f4efe4]"
              style={{ fontFamily: 'DM Serif Display, serif' }}
            >
              {demo.finalStatement}
            </p>
            <Link
              href={demo.ctaHref}
              className={`mt-6 inline-flex min-h-12 items-center justify-center gap-2 rounded-[9px] px-6 py-3 text-sm font-bold text-[#0a1614] ${
                motion ? 'transition-transform duration-150 ease-out active:scale-[0.98]' : ''
              } ${FOCUS_RING}`}
              style={{ background: 'linear-gradient(135deg,#1d9e75,#0f6e56)' }}
            >
              {demo.ctaLabel}
              <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
